import { data, useLoaderData, useNavigate } from 'react-router'
import { BusinessPagination } from '#app/components/business-pagination.tsx'
import { FilterCombobox } from '#app/components/filter-combobox.tsx'
import { RentalCard } from '#app/components/rental-card.tsx'
import { RentalSearch } from '#app/components/rental-search.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/city.$citySlug.ts'

const PAGE_SIZE = 36

export async function loader({ request, params }: Route.LoaderArgs) {
	const { citySlug } = params

	const url = new URL(request.url)
	const pageParam = Number(url.searchParams.get('page') || '1')
	const q = url.searchParams.get('q')?.trim() || ''
	const rawPage =
		Number.isFinite(pageParam) && pageParam > 0 ? Math.floor(pageParam) : 1

	// First fetch the city
	const city = await prisma.city.findUnique({
		where: {
			slug: citySlug,
			rentalListings: { some: { status: 'published' } },
		},
		select: {
			id: true,
			name: true,
			slug: true,
		},
	})

	if (!city) {
		throw new Response('City not found', { status: 404 })
	}

	// Then fetch categories that have rental listings in this city
	const categories = await prisma.category.findMany({
		where: {
			rentalListings: {
				some: {
					status: 'published',
					cities: { some: { id: city.id } },
				},
			},
		},
		select: {
			id: true,
			name: true,
			slug: true,
		},
		orderBy: {
			name: 'asc',
		},
	})

	const baseWhere = {
		cities: { some: { id: city.id } },
		status: 'published' as const,
	}
	const where =
		q.length > 0
			? {
					AND: [
						baseWhere,
						{
							OR: [
								{ title: { contains: q, mode: 'insensitive' as const } },
								{ description: { contains: q, mode: 'insensitive' as const } },
							],
						},
					],
				}
			: baseWhere

	const total = await prisma.rentalListing.count({ where })

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
	const page = Math.min(rawPage, totalPages)
	const skip = (page - 1) * PAGE_SIZE

	const rentals = await prisma.rentalListing.findMany({
		where,
		select: {
			id: true,
			title: true,
			slug: true,
			description: true,
			hourlyRateCents: true,
			dailyRateCents: true,
			weeklyRateCents: true,
			monthlyRateCents: true,
			featuredMedia: {
				select: {
					url: true,
					altText: true,
				},
			},
			categories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			subcategories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			cities: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			ownerBusiness: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			ownerUser: {
				select: {
					id: true,
					name: true,
				},
			},
		},
		orderBy: [{ createdAt: 'desc' }, { title: 'asc' }],
		skip,
		take: PAGE_SIZE,
	})

	return data({
		city,
		rentals,
		categories,
		page,
		pageSize: PAGE_SIZE,
		total,
	})
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb({ data }: { data: Awaited<ReturnType<typeof loader>>['data'] }) {
		const city = data?.city
		return city
			? { name: `Rentals in ${city.name}`, url: `/rentals/city/${city.slug}` }
			: null
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		const city = loaderData?.city
		if (!city) return null
		const canonical = `${origin}/rentals/city/${city.slug}`

		const structuredData =
			loaderData.rentals.length > 0
				? [
						buildItemListLd({
							name: `Rentals in ${city.name}`,
							items: loaderData.rentals.map((r) => ({
								name: r.title,
								url: `${origin}/rentals/${r.slug}`,
							})),
						}),
					]
				: undefined

		return {
			title: `Rentals in ${city.name} - RentDFW.com`,
			description: `Browse rental listings in ${city.name}. Find equipment, tools, and more available to rent today.`,
			canonical,
			openGraph: {
				title: `Rentals in ${city.name} - RentDFW.com`,
				description: `Browse rental listings in ${city.name}.`,
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: `Rentals in ${city.name} - RentDFW.com`,
				description: `Browse rental listings in ${city.name}.`,
			},
			structuredData,
		}
	},
}

export default function RentalsCityPage() {
	const { city, rentals, categories, page, pageSize, total } =
		useLoaderData<typeof loader>()
	const navigate = useNavigate()

	const totalPages = Math.max(1, Math.ceil(total / pageSize))

	function handleCategoryChange(value: string) {
		void navigate(`/rentals/${city.slug}/${value}`)
	}

	return (
		<>
			{/* SEO is handled by SeoHead via handle.seo */}
			<div className="container mx-auto px-4 py-16">
				<div className="mb-8">
					<div className="mb-4 flex flex-col gap-4">
						<div className="mx-auto max-w-3xl text-center">
							<h1 className="text-4xl font-semibold text-balance">
								Rentals in {city.name}
							</h1>
							<p className="text-muted-foreground mt-2 mb-8 text-xl">
								Explore a wide range of rentals available in {city.name}.
								Support local businesses and find the perfect equipment, tools,
								or event essentials right here in town.
							</p>
						</div>

						<div className="flex flex-col gap-3 md:flex-row">
							<div className="w-full">
								<RentalSearch total={total} />
							</div>
							{categories.length > 0 && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={categories.map((category) => ({
											label: category.name,
											value: category.slug,
										}))}
										onValueChange={handleCategoryChange}
										placeholder="Filter by category"
										searchPlaceholder="Search categories..."
										emptyText="No categories found"
									/>
								</div>
							)}
						</div>
					</div>
				</div>

				{total === 0 ? (
					<div className="bg-muted rounded-lg py-16 text-center">
						<Icon
							name="file-text"
							className="text-muted-foreground mx-auto mb-4 size-12"
						/>
						<h3 className="mb-2 text-xl font-semibold">No Rentals Available</h3>
						<p className="text-muted-foreground">
							No rental listings in {city.name} at the moment.
						</p>
					</div>
				) : (
					<>
						<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
							{rentals.map((rental) => (
								<RentalCard key={rental.id} rental={rental} />
							))}
						</div>

						<BusinessPagination currentPage={page} totalPages={totalPages} />
					</>
				)}
			</div>
		</>
	)
}
